// indonesiaV2Copy.ts
// ─── Astria Indonesia v2 — bilingual (EN/ID) UI copy ───────────────────────
// All fixed UI strings for the shell header, tab bar, and the tab screen's
// chrome (loading/reset/submit/CTA text). The client-approved backend copy
// pack response text itself is NOT translated here — the matching language
// is selected on the backend via `wizard.lang` (see
// helper/astriaIndonesiaV2Service.js's EN_COPY_PACK/ID_COPY_PACK). This file
// only localizes the surrounding UI chrome. Same pattern as
// vietnamV2Copy.ts.
import { IndonesiaV2Lang } from "./indonesiaV2Language";
import { AstriaIndonesiaV2Tab } from "./AstriaIndonesiaV2Shell";

interface ShellCopy {
  title: string;
  subtitle: string;
  langToggleLabel: string;
}

interface TabBarCopy {
  label: string;
  sublabel: string;
}

interface TabScreenCopy {
  question: string;
  helper: string;
  fields: Record<
    string,
    {
      label: string;
      options?: Record<string, string>;
      placeholder?: string;
      lowLabel?: string;
      highLabel?: string;
    }
  >;
}

interface IndonesiaV2CopyShape {
  shell: ShellCopy;
  tabBar: Record<AstriaIndonesiaV2Tab, TabBarCopy>;
  screens: Record<AstriaIndonesiaV2Tab, TabScreenCopy>;
  submitButton: string;
  loadingLabel: string;
  resetLink: string;
}

export const INDONESIA_V2_COPY: Record<IndonesiaV2Lang, IndonesiaV2CopyShape> = {
  id: {
    shell: {
      title: "Astria Indonesia",
      subtitle: "Ruang hangat untuk memeriksa perasaanmu",
      langToggleLabel: "Bahasa",
    },
    tabBar: {
      soft_summary: { label: "Ringkasan Lembut", sublabel: "Ringkasan Lembut" },
      connection_atmosphere: { label: "Suasana Koneksi", sublabel: "Suasana Koneksi" },
      timing_rhythm: { label: "Irama Waktu", sublabel: "Irama Waktu" },
      emotion_balance: { label: "Keseimbangan Emosi", sublabel: "Keseimbangan Emosi" },
      guidance_path: { label: "Arah Lembut", sublabel: "Arah Lembut" },
      your_note: { label: "Catatan Kamu", sublabel: "Catatan Kamu" },
    },
    screens: {
      soft_summary: {
        question: "Perasaan kamu hari ini seperti apa?",
        helper: "Sebuah jeda lembut untuk memeriksa perasaanmu saat ini.",
        fields: {
          mood: { label: "Mood" },
          intensity: {
            label: "Seberapa kuat perasaan itu?",
            lowLabel: "Lemah",
            highLabel: "Kuat",
          },
        },
      },
      connection_atmosphere: {
        question: "Suasana di antara kalian bagaimana?",
        helper: "Kehangatan (atau jarak) yang kamu rasakan dengan orang itu.",
        fields: {
          connection: {
            label: "Suasana di antara kalian",
            options: {
              warm: "Hangat",
              distant: "Berjarak",
              tense: "Tegang",
              playful: "Ceria",
              quiet: "Tenang",
            },
          },
          tone: {
            label: "Nada obrolan kalian",
            options: {
              gentle: "Lembut",
              guarded: "Waspada",
              open: "Terbuka",
              unsure: "Ragu",
            },
          },
        },
      },
      timing_rhythm: {
        question: "Irama saat ini bagaimana?",
        helper: "Kecepatan dan kesiapan momen ini.",
        fields: {
          flow: {
            label: "Irama saat ini",
            options: {
              slow: "Pelan",
              steady: "Stabil",
              rushed: "Terburu-buru",
              uncertain: "Belum pasti",
            },
          },
          readiness: {
            label: "Seberapa siap kamu?",
            lowLabel: "Belum siap",
            highLabel: "Siap",
          },
        },
      },
      emotion_balance: {
        question: "Zona emosi kamu sekarang bagaimana?",
        helper: "Di mana emosimu berada saat ini.",
        fields: {
          emotion_zone: {
            label: "Zona emosi kamu sekarang",
            options: {
              warm: "Hangat",
              heavy: "Berat",
              light: "Ringan",
              tender: "Lembut",
              steady: "Stabil",
            },
          },
          location: {
            label: "Perasaan apa yang paling terasa?",
            options: {
              chest: "Dada",
              mind: "Pikiran",
              stomach: "Perut",
              everywhere: "Semua",
            },
          },
        },
      },
      guidance_path: {
        question: "Apa yang sedang terjadi?",
        helper: "Langkah lembut untuk yang sedang kamu hadapi.",
        fields: {
          issue: {
            label: "Apa yang sedang terjadi?",
            options: {
              communication: "Komunikasi",
              distance: "Jarak",
              uncertainty: "Ketidakpastian",
              overwhelm: "Kewalahan",
            },
          },
          desired: {
            label: "Apa yang kamu harapkan?",
            options: {
              clarity: "Kejelasan",
              closeness: "Kedekatan",
              peace: "Ketenangan",
              courage: "Keberanian",
            },
          },
        },
      },
      your_note: {
        question: "Apa yang ada di pikiranmu?",
        helper: "Refleksi lembut atas pesan terakhirmu.",
        fields: {
          latest_message: {
            label: "Pesan terakhir kamu",
            placeholder: "Apa yang ingin kamu katakan?",
          },
          feeling: {
            label: "Apa yang kamu rasakan saat menulis itu?",
            options: {
              soft: "Lembut",
              hopeful: "Berharap",
              unsure: "Ragu",
              relieved: "Lega",
            },
          },
        },
      },
    },
    submitButton: "Tampilkan",
    loadingLabel: "Membaca perasaanmu…",
    resetLink: "Mulai check-in baru",
  },
  en: {
    shell: {
      title: "Astria Indonesia",
      subtitle: "A warm, gentle space to check in with how you're feeling",
      langToggleLabel: "Language",
    },
    tabBar: {
      soft_summary: { label: "Soft Summary", sublabel: "Ringkasan Lembut" },
      connection_atmosphere: { label: "Connection Atmosphere", sublabel: "Suasana Koneksi" },
      timing_rhythm: { label: "Timing Rhythm", sublabel: "Irama Waktu" },
      emotion_balance: { label: "Emotion Balance", sublabel: "Keseimbangan Emosi" },
      guidance_path: { label: "Guidance Path", sublabel: "Arah Lembut" },
      your_note: { label: "Your Note", sublabel: "Catatan Kamu" },
    },
    screens: {
      soft_summary: {
        question: "How are you feeling right now?",
        helper: "A gentle check-in on how you're feeling right now.",
        fields: {
          mood: { label: "Mood" },
          intensity: {
            label: "How strong is that feeling?",
            lowLabel: "Light",
            highLabel: "Strong",
          },
        },
      },
      connection_atmosphere: {
        question: "What's the atmosphere between you two?",
        helper: "The warmth (or distance) you're feeling with someone else.",
        fields: {
          connection: {
            label: "Atmosphere between you two",
            options: {
              warm: "Warm",
              distant: "Distant",
              tense: "Tense",
              playful: "Playful",
              quiet: "Quiet",
            },
          },
          tone: {
            label: "Their tone",
            options: {
              gentle: "Gentle",
              guarded: "Guarded",
              open: "Open",
              unsure: "Unsure",
            },
          },
        },
      },
      timing_rhythm: {
        question: "What's the rhythm of this moment?",
        helper: "The pace and readiness of this moment.",
        fields: {
          flow: {
            label: "Current flow",
            options: {
              slow: "Slow",
              steady: "Steady",
              rushed: "Rushed",
              uncertain: "Uncertain",
            },
          },
          readiness: {
            label: "How ready do you feel?",
            lowLabel: "Not yet",
            highLabel: "Ready",
          },
        },
      },
      emotion_balance: {
        question: "Where are your emotions sitting?",
        helper: "Where your emotions are sitting right now.",
        fields: {
          emotion_zone: {
            label: "Emotion zone",
            options: {
              warm: "Warm",
              heavy: "Heavy",
              light: "Light",
              tender: "Tender",
              steady: "Steady",
            },
          },
          location: {
            label: "Where does it show up most?",
            options: {
              chest: "Chest",
              mind: "Mind",
              stomach: "Stomach",
              everywhere: "Everywhere",
            },
          },
        },
      },
      guidance_path: {
        question: "What's going on?",
        helper: "A soft next step for what you're facing.",
        fields: {
          issue: {
            label: "What's going on",
            options: {
              communication: "Communication",
              distance: "Distance",
              uncertainty: "Uncertainty",
              overwhelm: "Overwhelm",
            },
          },
          desired: {
            label: "What do you want?",
            options: {
              clarity: "Clarity",
              closeness: "Closeness",
              peace: "Peace",
              courage: "Courage",
            },
          },
        },
      },
      your_note: {
        question: "What's on your mind?",
        helper: "A gentle reflection on your latest message.",
        fields: {
          latest_message: {
            label: "Your latest message",
            placeholder: "What would you like to say?",
          },
          feeling: {
            label: "How did you feel while writing it?",
            options: {
              soft: "Soft",
              hopeful: "Hopeful",
              unsure: "Unsure",
              relieved: "Relieved",
            },
          },
        },
      },
    },
    submitButton: "Reveal",
    loadingLabel: "Reading your check-in…",
    resetLink: "Start a new check-in",
  },
};
